import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CalendarDays } from "lucide-react";
import confetti from "canvas-confetti";
import { scrollToId } from "@/lib/scroll";
import { useEventState } from "@/lib/eventWindow";

const GOLD = ["#D4AF37", "#8B0000", "#FDFBF7"];

function PulseDot() {
  return (
    <span className="relative flex h-2 w-2 shrink-0">
      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-ivory opacity-75" />
      <span className="relative inline-flex h-2 w-2 rounded-full bg-ivory" />
    </span>
  );
}

function LivePill({ day }) {
  return (
    <span
      className="relative flex items-center gap-2.5 overflow-hidden rounded-full bg-crimson px-5 py-3 shadow-[0_8px_30px_rgba(139,0,0,0.45)]"
      data-testid="live-badge-live"
    >
      <span className="sheen pointer-events-none absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/25 to-transparent" />
      <PulseDot />
      <span className="gold-on-crimson relative whitespace-nowrap font-display text-[10px] font-bold tracking-[0.14em] text-gold sm:text-xs">
        DAY {day} OF 3 — FOUNDER'S DAY IS LIVE
      </span>
    </span>
  );
}

function BeforePill() {
  return (
    <span
      className="glass flex items-center gap-2.5 rounded-full px-5 py-3 opacity-90"
      data-testid="live-badge-before"
    >
      <CalendarDays className="h-3.5 w-3.5 shrink-0 text-gold" />
      <span className="whitespace-nowrap font-mono text-[10px] tracking-[0.14em] text-soft sm:text-[11px]">
        FOUNDER'S DAY '26 • OCT 22–24
      </span>
    </span>
  );
}

function AfterPill() {
  return (
    <span
      className="glass flex items-center gap-2.5 rounded-full px-5 py-3"
      data-testid="live-badge-after"
    >
      <span className="h-2 w-2 shrink-0 rounded-full bg-gold" />
      <span className="whitespace-nowrap font-display text-[10px] tracking-[0.14em] text-gold sm:text-xs">
        THAT'S A WRAP — SEE YOU FOR THE 92ND IN 2027
      </span>
    </span>
  );
}

export default function LiveBadge() {
  const ev = useEventState();
  const fired = useRef(false);
  const [heroVisible, setHeroVisible] = useState(true);

  useEffect(() => {
    const home = document.getElementById("home");
    if (!home || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver((entries) => entries.forEach((e) => setHeroVisible(e.isIntersecting)), { threshold: 0.35 });
    io.observe(home);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (ev.phase !== "live" || fired.current) return;
    fired.current = true;
    const t = setTimeout(() => {
      confetti({ particleCount: 130, spread: 100, origin: { x: 0.1, y: 0.9 }, colors: GOLD, zIndex: 9998 });
      setTimeout(() => confetti({ particleCount: 80, spread: 80, origin: { x: 0.12, y: 0.85 }, colors: GOLD, zIndex: 9998 }), 280);
    }, 1600);
    return () => clearTimeout(t);
  }, [ev.phase]);

  const go = () => scrollToId(ev.phase === "after" ? "#home" : "#schedule");

  return (
    <motion.div
      initial={{ y: 90, opacity: 0 }}
      animate={{
        y: heroVisible ? 140 : 0,
        opacity: heroVisible ? 0 : 1,
      }}
      transition={{ delay: heroVisible ? 0 : 1.4, type: "spring", stiffness: 120, damping: 16 }}
      className={`fixed bottom-5 left-5 z-[60] sm:bottom-6 sm:left-6 ${heroVisible ? "pointer-events-none" : ""}`}
      data-testid="live-badge"
    >
      <button
        onClick={go}
        aria-label={ev.phase === "live" ? "Founder's Day is live — view schedule" : "Founder's Day status"}
        className="group block cursor-pointer transition-transform duration-300 hover:scale-[1.04]"
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={ev.phase}
            initial={{ opacity: 0, scale: 0.85, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.85, y: -12 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="block"
          >
            {ev.phase === "live" ? <LivePill day={ev.day} /> : ev.phase === "after" ? <AfterPill /> : <BeforePill />}
          </motion.span>
        </AnimatePresence>
      </button>
    </motion.div>
  );
}
